import { useNavigate } from "react-router-dom";
import { useProductionLocations } from "@/hooks/useProductionLocations";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { Monitor, ChefHat, Coffee, Package, BarChart3 } from "lucide-react";

export default function KdsSelectStation() {
  const navigate = useNavigate();
  const { locations, isLoading } = useProductionLocations();
  const { activeRestaurant } = useRestaurantContext();
  const activeLocations = locations.filter(l => l.is_active);

  const getIcon = (name: string) => {
    const lower = name.toLowerCase();
    if (lower.includes("cozinha") || lower.includes("kitchen")) return ChefHat;
    if (lower.includes("bar") || lower.includes("bebida")) return Coffee;
    if (lower.includes("expo") || lower.includes("expedi")) return Package;
    return Monitor;
  };

  return (
    <div className="h-full flex flex-col items-center justify-center p-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-white mb-2">KDS - {activeRestaurant?.name}</h1>
        <p className="text-zinc-400 text-lg">Selecione a estação de produção</p>
      </div>

      {isLoading ? (
        <p className="text-zinc-400">Carregando estações...</p>
      ) : activeLocations.length === 0 ? (
        <div className="text-center">
          <p className="text-zinc-400 mb-2">Nenhum local de produção ativo encontrado.</p>
          <p className="text-zinc-500 text-sm">Configure locais de produção no dashboard em Configurações → Locais de Produção.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl w-full">
          {activeLocations.map((location) => {
            const Icon = getIcon(location.name);
            return (
              <button
                key={location.id}
                onClick={() => navigate(`/kds/station/${location.id}`)}
                className="group flex flex-col items-center gap-4 p-8 rounded-2xl bg-zinc-800 border-2 border-zinc-700 hover:border-emerald-500 hover:bg-zinc-800/80 transition-all duration-200"
              >
                <div className="w-16 h-16 rounded-xl bg-zinc-700 group-hover:bg-emerald-500/20 flex items-center justify-center transition-colors">
                  <Icon className="w-8 h-8 text-zinc-300 group-hover:text-emerald-400 transition-colors" />
                </div>
                <span className="text-xl font-semibold text-white">{location.name}</span>
              </button>
            );
          })}

          {/* Expo button */}
          <button
            onClick={() => navigate("/kds/expo")}
            className="group flex flex-col items-center gap-4 p-8 rounded-2xl bg-zinc-800 border-2 border-amber-700/50 hover:border-amber-500 hover:bg-zinc-800/80 transition-all duration-200"
          >
            <div className="w-16 h-16 rounded-xl bg-amber-900/30 group-hover:bg-amber-500/20 flex items-center justify-center transition-colors">
              <Package className="w-8 h-8 text-amber-400 group-hover:text-amber-300 transition-colors" />
            </div>
            <span className="text-xl font-semibold text-amber-300">Expedição</span>
          </button>

          {/* Dashboard button */}
          <button
            onClick={() => navigate("/kds/dashboard")}
            className="group flex flex-col items-center gap-4 p-8 rounded-2xl bg-zinc-800 border-2 border-sky-700/50 hover:border-sky-500 hover:bg-zinc-800/80 transition-all duration-200"
          >
            <div className="w-16 h-16 rounded-xl bg-sky-900/30 group-hover:bg-sky-500/20 flex items-center justify-center transition-colors">
              <BarChart3 className="w-8 h-8 text-sky-400 group-hover:text-sky-300 transition-colors" />
            </div>
            <span className="text-xl font-semibold text-sky-300">Dashboard</span>
          </button>
        </div>
      )}
    </div>
  );
}
